import { useMemo } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ShieldCheck, ShieldAlert, Shield } from 'lucide-react';
import type { DemoDomain } from '@/demo/demoFixtures';
import { STATUS_LABELS } from '@/lib/vocabulary/securityTerminology';

const statusMeta = (status: DemoDomain['health_status']) => {
  switch (status) {
    case 'healthy':
      return { label: STATUS_LABELS.covered, tone: 'success' as const, icon: ShieldCheck };
    case 'high_risk':
      return { label: STATUS_LABELS.actionRequired, tone: 'danger' as const, icon: ShieldAlert };
    case 'attention':
      return { label: STATUS_LABELS.partial, tone: 'warning' as const, icon: ShieldAlert };
    default:
      return { label: STATUS_LABELS.monitoringPlanned, tone: 'neutral' as const, icon: Shield };
  }
};

export default function DemoDomainOverview() {
  const { domain } = useOutletContext<{ domain: DemoDomain }>();
  const navigate = useNavigate();
  const meta = statusMeta(domain.health_status);
  const MetaIcon = meta.icon;

  const topFindings = useMemo(() => {
    const labelMap: Record<string, string> = {
      A01: 'Account & access protection',
      A02: 'Transport & crypto posture',
      A03: 'Input safety signals',
      A04: 'Security guardrails',
      A05: 'Configuration hygiene',
      A06: 'Updates & integrity',
      A07: 'Authentication hardening',
      A08: 'Integrity checks',
      A09: 'Monitoring readiness',
      A10: 'Request controls',
    };
    return domain.owasp
      .filter((category) => category.status === 'critical' || category.status === 'warn')
      .slice(0, 3)
      .map((category) => labelMap[category.id] || category.name);
  }, [domain.owasp]);

  return (
    <div className="grid gap-6">
      <Card className="border-border/60">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MetaIcon className="h-5 w-5" />
            Security Health
          </CardTitle>
          <CardDescription>Quick readiness snapshot based on demo protection coverage signals.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Badge variant={meta.tone}>{meta.label}</Badge>
            <span className="text-sm text-muted-foreground">Policy tier: {domain.policy_tier}</span>
          </div>
          <div className="text-sm text-muted-foreground">Issues requiring action: {domain.issues_requiring_action}</div>
          <div className="text-sm text-muted-foreground">Containment: {domain.containment_state}</div>
          <div className="rounded-md border border-border/60 bg-muted/20 p-3 text-sm">
            <div className="font-semibold">Top items</div>
            {topFindings.length === 0 ? (
              <div className="text-muted-foreground">No active demo findings.</div>
            ) : (
              <ul className="list-disc list-inside text-muted-foreground">
                {topFindings.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/60">
        <CardHeader>
          <CardTitle>Guided Demo</CardTitle>
          <CardDescription>Walk through the demo story in a few clicks.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <span>Step 1: Review protection coverage for this demo site.</span>
            <Button size="sm" variant="outline" onClick={() => navigate(`/portal/demo/domains/${domain.site_id}/coverage`)}>
              Go
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <span>Step 2: Inspect Security Activity evidence.</span>
            <Button size="sm" variant="outline" onClick={() => navigate(`/portal/demo/domains/${domain.site_id}/logs`)}>
              Go
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <span>Step 3: Export demo evidence (simulated).</span>
            <Button size="sm" variant="outline" onClick={() => navigate(`/portal/demo/domains/${domain.site_id}/evidence`)}>
              Go
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
